import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { ErrorBoundary } from 'bernie-view';

import {
  LodgingOnSubmitCallback,
  LodgingQueryParamsProvider,
  SearchForm,
  SearchFormInputs,
  useLodgingURLParamsToQueryInputs,
} from '@shared-ui/search-tools-lodging';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useLocalization } from '@shared-ui/localization-context';

import { SearchResultsStore } from 'src/common/stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';

import { HOUSE_RULES_GROUP } from 'src/common/constants/filters';

import {
  isMetaChannelDefaultDatesSearch,
  isSEMChannelDefaultDatesSearch,
  isSRPAutoOpenPlaybackExperiment,
  isSRPCalendarAutoOpenExperiment,
  isVrbexExperiment,
  updateResultsWithNewCriteria,
} from './universal-search-form-utils';

import { EssClientLogger } from 'typeahead-client-logger';
import { parse } from 'query-string';

export interface UniversalSearchFormWrapperProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  uiPage?: UIPageStore;
  context?: ContextStore;
}

const UniversalSearchFormWrapper: React.FC<UniversalSearchFormWrapperProps> = inject(
  'analytics',
  'searchResults',
  'experiment',
  'uiPage',
  'context'
)(
  observer(({ analytics, searchResults, experiment, uiPage, context }: UniversalSearchFormWrapperProps) => {
    const location = useLocation();
    const history = useHistory();
    const l10n = useLocalization();

    const [datePickerOpen, setDatePickerOpen] = React.useState(false);

    const searchQueryParams = new URLSearchParams(location.search);
    const queryParamStartDate = searchQueryParams.get('startDate') || '';
    const queryParamEndDate = searchQueryParams.get('endDate') || '';
    const isMobile = context?.deviceInformation?.mobile && !context?.deviceInformation?.tablet;
    const isMetaOrSEMChannelDefaultDatesSearch = Boolean(
      isMetaChannelDefaultDatesSearch(searchQueryParams) || isSEMChannelDefaultDatesSearch(searchQueryParams)
    );
    const isSRPCalendarAutoOpenExperimentEnabled = Boolean(experiment && context && isSRPCalendarAutoOpenExperiment(experiment, context));
    const isSRPAutoOpenPlaybackExperimentEnabled = Boolean(experiment && context && isSRPAutoOpenPlaybackExperiment(experiment, context));

    // Auto open calendar when user lands on SRP from META/SEM channel and had not entered dates
    React.useEffect(() => {
      if (isMetaOrSEMChannelDefaultDatesSearch && isSRPCalendarAutoOpenExperimentEnabled && !isMobile) {
        setDatePickerOpen(true);
      }
    }, []);

    // Auto open calender for dateless SRP for vrbex
    React.useEffect(() => {
      if (experiment && isVrbexExperiment(experiment) && !isMobile) {
        if (!queryParamStartDate && !queryParamEndDate) {
          setDatePickerOpen(true);
        }
      }
    }, []);

    React.useEffect(() => {
      const uiPageOpenDatePickerDisposer = reaction(
        () => uiPage?.openNewDatePickerEvent,
        openNewDatePickerEvent => {
          if (openNewDatePickerEvent) {
            setDatePickerOpen(true);
          }
        }
      );

      return () => uiPageOpenDatePickerDisposer();
    }, [uiPage?.openNewDatePickerEvent]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>, inputs?: SearchFormInputs) => {
      event.preventDefault();
      analytics?.resetAnalyticsForNewPage();

      if (searchResults && experiment && inputs) {
        updateResultsWithNewCriteria(inputs, location, history, searchResults, experiment, l10n);
      }
    };

    const essLogger = new EssClientLogger(analytics, ClientLogger, searchResults?.searchCriteria.destination, experiment?.exposures);

    return (
      <ErrorBoundary label="hotel.search.universal-search-form">
        <LodgingQueryParamsProvider urlSearchParams={new URLSearchParams(useLocation().search)}>
          <SearchFormWrapping
            handleSubmit={handleSubmit}
            essLogger={essLogger}
            showDatePicker={datePickerOpen}
            toggleDatePicker={setDatePickerOpen}
            autoOpenPlayback={isMetaOrSEMChannelDefaultDatesSearch && isSRPAutoOpenPlaybackExperimentEnabled}
          />
        </LodgingQueryParamsProvider>
      </ErrorBoundary>
    );
  })
);

interface SearchFormWrappingProps {
  handleSubmit: LodgingOnSubmitCallback;
  essLogger: EssClientLogger;
  showDatePicker: boolean;
  autoOpenPlayback: boolean;
  toggleDatePicker: (shouldOpen: boolean) => void;
}

const SearchFormWrapping: React.FC<SearchFormWrappingProps> = ({
  handleSubmit,
  essLogger,
  showDatePicker,
  toggleDatePicker,
  autoOpenPlayback,
}) => {
  const inputs = useLodgingURLParamsToQueryInputs();
  const history = useHistory();
  const [skipQuery, setSkipQuery] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const isAnyPetAllowed = inputs?.propertySearchCriteriaInput?.secondary?.selections?.some(
    selection => selection.value === HOUSE_RULES_GROUP.PETS_ALLOWED
  );

  React.useEffect(() => {
    /* istanbul ignore next */
    if (history.action === 'POP' && !isDialogOpen) {
      setSkipQuery(false);
    } else if (inputs && !skipQuery) {
      setSkipQuery(true);
    }
    setIsDialogOpen(Boolean(parse(history.location.search).pwaDialog));
  }, [inputs, skipQuery, history.action]);

  return (
    <Experiment name="Lodging_Traveler_Selector_Interaction_Update_SRP">
      <ExperimentControl>
        <SearchForm
          inputs={inputs ?? {}}
          onSubmit={handleSubmit}
          includePlayback
          autoOpenPlayback={autoOpenPlayback}
          skipQuery={skipQuery}
          essLogger={essLogger}
          showCalendar={showDatePicker}
          toggleCalendar={toggleDatePicker}
          autoSubmitOnFieldChange
          isAnyPetAllowed={isAnyPetAllowed}
        />
      </ExperimentControl>
      <ExperimentVariant bucket={1}>
        <div data-stid="retain-traveler-count">
          <SearchForm
            inputs={inputs ?? {}}
            onSubmit={handleSubmit}
            includePlayback
            autoOpenPlayback={autoOpenPlayback}
            skipQuery={skipQuery}
            essLogger={essLogger}
            showCalendar={showDatePicker}
            toggleCalendar={toggleDatePicker}
            autoSubmitOnFieldChange
            isAnyPetAllowed={isAnyPetAllowed}
            retainTravelerCount
          />
        </div>
      </ExperimentVariant>
    </Experiment>
  );
};

export const SharedUiUniversalSearchFormWrapper = UniversalSearchFormWrapper;
SharedUiUniversalSearchFormWrapper.displayName = 'SharedUiUniversalSearchFormWrapper';
